import { Button, ButtonVariant, Card } from "@modelve/ui";
import clsx from "clsx";
import { useKnowledgeStore } from "components/knowledge/store";
import { BookOpen, ChevronDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { StackableItemProps } from "../types";
import { useToggleKnowledge } from "components/knowledge/hooks";

type BannerType = "feature" | "warning";

export interface PageBannerProps extends StackableItemProps {
  title: string;
  description: string;
  type?: "feature" | "warning";
  article?: string;
  close?: () => void;
}

const ButtonVariantLookup: Record<BannerType, ButtonVariant> = {
  feature: "primary",
  warning: "warning",
};

export default function PageBanner(props: Readonly<PageBannerProps>) {
  const {
    title,
    description,
    type = "feature",
    article,
    close,
    index,
    count,
    expanded,
    toggleExpanded,
  } = props;

  const { t } = useTranslation();
  const footerComponents: React.ReactNode[] = [];
  const push = useToggleKnowledge();

  if (article?.trim().length) {
    footerComponents.push(
      <Button
        key="article"
        size="sm"
        mode="flat"
        onClick={() => push(article)}
        variant={ButtonVariantLookup[type]}
      >
        <BookOpen size="16" /> {t("common.button.read_more", "Read More")}
      </Button>,
    );
  }

  if (close) {
    footerComponents.push(
      <Button
        className="ms-auto"
        key="dismiss"
        size="sm"
        mode="flat"
        onClick={close}
        variant={ButtonVariantLookup[type]}
      >
        <X size="16" /> {t("common.button.dismiss", "Dismiss")}
      </Button>,
    );
  }

  if (index === count - 1 && count > 1) {
    footerComponents.push(
      <Button
        key="expand"
        size="sm"
        mode="flat"
        variant={ButtonVariantLookup[type]}
        onClick={toggleExpanded}
      >
        <ChevronDown
          size="16"
          className={expanded ? "rotate-180" : undefined}
        />
      </Button>,
    );
  }

  return (
    <Card
      className={clsx("shadow-xl rounded-xl border-l-8 transition-all", {
        "shadow-primary/15 border-l-primary/50": type === "feature",
        "shadow-amber-600/15 border-l-amber-600/50": type === "warning",
      })}
    >
      <Card.Header
        className={clsx("text-primary-dark whitespace-pre-line", {
          "pb-0!": !!footerComponents.length,
        })}
      >
        <Card.Title>{title}</Card.Title>
        <Card.SubTitle>{description}</Card.SubTitle>
      </Card.Header>
      {!!footerComponents.length && (
        <Card.Footer>{footerComponents}</Card.Footer>
      )}
    </Card>
  );
}
